import { useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { AdminPermissions } from 'constants/admin-permissions';
import { MESSAGE_STATE_ERROR } from 'constants/message-app-state-contants';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { Tab, Tabs } from 'react-bootstrap';
import cookie from 'react-cookies';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  DatePicker,
  Input,
  InputGroup,
  Select,
  ToggleSwitch,
} from 'components/FormComponents';
import { RowDetails } from 'components/RowDetails/RowDetails';

import { messagePopUp } from 'api/app-slice';
import { loginAs } from 'api/authentication-slice';
import { selectAuthentication } from 'api/authentication-slice';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import {
  useGetCompanyBillingInfoQuery,
  useGetCompanyChecksQuery,
  useGetCompanyOwnerQuery,
  useGetCompanyQuery,
  useGetCompanyTeamsQuery,
  useGetCompanyUsersQuery,
  useLazyGetCompanyUsageReportQuery,
  useUpdateCompanyMutation,
} from 'api/company-api';
import {
  useCreateCompanyCheckMutation,
  useDeleteCompanyCheckMutation,
  useUpdateCompanyCheckMutation,
} from 'api/company-checks-api';
import { useCreatePurchaseMutation } from 'api/purchases-api';

import styles from './AdminCompany.module.scss';
import { ChecksTab } from './tabs/ChecksTab/ChecksTab';

const AdminCompany = () => {
  let { company_id: companyId } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authentication = useSelector(selectAuthentication);

  const { data: companyData, refetch: refetchCompany } = useGetCompanyQuery(companyId, {
    refetchOnMountOrArgChange: true,
  });
  const { data: user } = useGetCompanyOwnerQuery(companyId);
  const { data: usersData } = useGetCompanyUsersQuery({ id: companyId });
  const { data: checkTypes } = useGetCheckTypesQuery();
  const { data: billingInfo } = useGetCompanyBillingInfoQuery(companyId);
  const {
    data: companyChecksData,
    isLoading: loadingCompanyChecks,
    refetch: refetchCompanyChecks,
  } = useGetCompanyChecksQuery(companyId, {
    refetchOnMountOrArgChange: true,
  });

  const { data: companyTeamsData } = useGetCompanyTeamsQuery(companyId, {
    skip: !companyId,
    refetchOnMountOrArgChange: true,
  });

  const [getCompanyUsageReport] = useLazyGetCompanyUsageReportQuery();
  const [updateCompanyCheck] = useUpdateCompanyCheckMutation();
  const [deleteCompanyCheck] = useDeleteCompanyCheckMutation();
  const [createCompanyCheck] = useCreateCompanyCheckMutation();
  const [companyUpdate] = useUpdateCompanyMutation();
  const [createPurchase] = useCreatePurchaseMutation();

  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState();
  const [companyChecks, setCompanyChecks] = useState();
  const [users, setUsers] = useState([]);

  const [showFiltersModal, setShowFiltersModal] = useState(false);

  useDocumentTitle(company ? company.name : 'Loading...');

  const hasPermission = useAccessControl(authentication.admin_permissions);

  const { watch, register, reset, handleSubmit } = useForm({
    disabled: !hasPermission(AdminPermissions.UpdateCompany),
  });

  useEffect(() => {
    if (!companyData) return;

    setCompany(companyData);
    reset(companyData);

    return () => {
      setCompany(null);
      setCompanyChecks([]);
    };
  }, [companyData]);

  watch('name');

  useEffect(() => {
    if (companyChecksData) setCompanyChecks(companyChecksData);
  }, [companyChecksData]);
  useEffect(() => {
    if (usersData) setUsers(usersData.result.users);
  }, [usersData]);

  const companyForChecksTab = useMemo(() => {
    if (!company) return null;
    const teams = companyTeamsData?.map(team => ({ value: team.id, name: team.name }));
    if (!teams) return company;
    return { ...company, teams };
  }, [company, companyTeamsData]);

  const onUpdate = async data => {
    setLoading(true);
    await companyUpdate({ id: companyId, data });
    refetchCompany();
    refetchCompanyChecks();
    setLoading(false);
  };

  const handleCompanyCheckUpdate = async (id, data = {}) => {
    setLoading(true);
    const { data: updatedCompanyCheck, error } = await updateCompanyCheck({ id, data });
    if (error) {
      dispatch(
        messagePopUp({ text: error.data.error.log, state: MESSAGE_STATE_ERROR, hide: true }),
      );
      setLoading(false);
      return false;
    }
    setCompanyChecks(
      companyChecks.map(companyCheck =>
        companyCheck.id === updatedCompanyCheck.id ? updatedCompanyCheck : companyCheck,
      ),
    );
    setLoading(false);
  };

  const toggleCompanyCheck = async (id, type, value) => {
    setLoading(true);
    if (value) {
      const { data: companyCheck, error } = await createCompanyCheck({
        company_id: companyId,
        type,
      });
      if (error) {
        dispatch(
          messagePopUp({ text: error.data.error.log, state: MESSAGE_STATE_ERROR, hide: true }),
        );
        setLoading(false);
        return false;
      }
      setCompanyChecks([...companyChecks, companyCheck]);
    } else {
      const { error } = await deleteCompanyCheck(id);
      if (error) {
        dispatch(
          messagePopUp({ text: error.data.error.log, state: MESSAGE_STATE_ERROR, hide: true }),
        );
        setLoading(false);
        return false;
      }
      setCompanyChecks(curr => curr.filter(companyCheck => companyCheck.id !== id));
    }
    setLoading(false);
    return true;
  };

  const handleLoginAs = async () => {
    setLoading(true);
    const { payload: result } = await dispatch(loginAs(user.id));
    cookie.save('admin-token', cookie.load('token'), { path: '/' });
    cookie.save('admin-route', `/admin/companies/${companyId}`, { path: '/' });
    cookie.save('token', result.token, { path: '/' });
    setLoading(false);
    navigate('/dashboard');
  };

  const downloadUsageReport = async filters => {
    setLoading(true);
    await getCompanyUsageReport({ id: companyId, data: filters });
    setLoading(false);
  };

  return (
    <AdminPage
      loading={loading || loadingCompanyChecks}
      title={company?.name}
      allowedRole={AdminPermissions.ListCompanies}>
      <ReportFiltersModal
        visible={showFiltersModal}
        onSubmit={downloadUsageReport}
        onClose={() => setShowFiltersModal(false)}
      />
      <RowDetails className={styles.details}>
        <RowDetails.Column title="ID" data={company?.id} />
        <RowDetails.Column title="Name" data={company?.name} />
        <RowDetails.Column title="Owner Name" data={user?.name} />
        <RowDetails.Column title="Owner Email" data={user?.email} />
        {hasPermission(AdminPermissions.LoginAsClient) && (
          <RowDetails.Action
            icon="key"
            tooltip="Login into company as admin"
            onClick={handleLoginAs}
          />
        )}
        {hasPermission(AdminPermissions.ListCompanies) && (
          <RowDetails.Action
            icon="file-text"
            tooltip="Download company usage report"
            onClick={() => setShowFiltersModal(true)}
          />
        )}
      </RowDetails>

      <Tabs defaultActiveKey="config" className="tabs no-padding padding-small">
        <Tab eventKey="config" title="Configuration" className="u-padding--large">
          <form
            className="card card-with-border u-padding u-width-25"
            onSubmit={handleSubmit(onUpdate)}>
            <ToggleSwitch
              register={register}
              name="checkmate_branding_disabled"
              label="Deactivate Checkmate branding"
            />
            <ToggleSwitch register={register} name="audit_logs_enabled" label="Audit Logs" />
            <ToggleSwitch
              register={register}
              name="renewal_dashboard_enabled"
              label="Renewal Dashboard"
            />
            <ToggleSwitch register={register} name="ff_ai_forms" label="AI Forms FF" />
            <ToggleSwitch
              register={register}
              name="ff_reporting_billing_filter"
              label="Reporting Billing Filter FF"
            />
            <ToggleSwitch register={register} name="use_volunteers" label="Use Volunteers" />
            <ToggleSwitch
              register={register}
              name="allow_portal_access"
              label="Allow Portal Access"
              tooltip="When off, the portal will be disabled for the candidates of this company."
            />
            <ToggleSwitch
              register={register}
              name="show_support_widget"
              label="Show Support Widget"
              tooltip="When off, the Intercom support widget will be hidden for this company."
            />
            <InputGroup title="User Type" className="u-width-100">
              <Select name="user_type" register={register} value={watch('user_type')} useDefault>
                <Select.Item value="casual">Casual PAYG</Select.Item>
                <Select.Item value="internal">Internal</Select.Item>
                <Select.Item value="enterprise">Enterprise</Select.Item>
              </Select>
            </InputGroup>
            <InputGroup title="Account Manager" className="u-width-100">
              <Select
                name="account_manager"
                register={register}
                value={watch('account_manager')}
                useDefault>
                {process.env.REACT_APP_ACCOUNT_MANAGERS.split(',').map(account_manager => (
                  <Select.Item
                    key={account_manager}
                    className="u-capitalize"
                    value={account_manager}>
                    {account_manager}
                  </Select.Item>
                ))}
                <Select.Item value="other">Other</Select.Item>
              </Select>
            </InputGroup>
            <InputGroup title="Support contact email">
              <Input
                name="contact_email"
                placeholder="support@checkmate.tech"
                register={register}
              />
            </InputGroup>

            {hasPermission(AdminPermissions.UpdateCompany) && (
              <Button className="u-width-100 u-margin-top--large" submit>
                Save
              </Button>
            )}
          </form>
        </Tab>
        <Tab eventKey="checks" title="Checks" className="u-padding--large">
          <ChecksTab
            company={companyForChecksTab ?? company}
            users={users}
            checkTypes={checkTypes}
            companyChecks={companyChecks}
            billingInfo={billingInfo}
            disableUpdates={!hasPermission(AdminPermissions.UpdateCompany)}
            setLoading={setLoading}
            onToggle={toggleCompanyCheck}
            onSave={handleCompanyCheckUpdate}
            onAddCredits={createPurchase}
          />
        </Tab>
      </Tabs>
    </AdminPage>
  );
};

export default AdminCompany;

const ReportFiltersModal = ({ visible, onSubmit = () => {}, onClose = () => {} }) => {
  const { control, handleSubmit } = useForm();

  return (
    <AnimatedModal title="Filters" visible={visible} onClose={onClose}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn('u-margin-top', styles.filtersModalForm)}>
        <InputGroup title="Date From">
          <Controller
            control={control}
            name="date_from"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <DatePicker value={value} onChange={onChange} outputFormat="YYYY-MM-DD" />
            )}
          />
        </InputGroup>
        <InputGroup title="Date To">
          <Controller
            control={control}
            name="date_to"
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <DatePicker value={value} onChange={onChange} outputFormat="YYYY-MM-DD" />
            )}
          />
        </InputGroup>
        <Button submit className="u-width-100 u-margin-top--large">
          Download
        </Button>
      </form>
    </AnimatedModal>
  );
};
